import { WritableSignal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { LoggerService } from 'app/services/logger.service';
import {
  ChatMessage,
  ChatSessionWithName,
  SpotlightAgentChatService,
} from 'app/services/spotlight-agent-chat.service';
import { SpotlightAssistantAskModeHelper } from './spotlight-assistant-ask-mode.helper';
import {
  OrchestratorChartOutput,
  StreamingChatDelegate,
  StreamingChatOrchestrator,
} from './streaming-chat-orchestrator';
import { ToolContent } from './tool-content.model';

interface AssistantMessageLike {
  readonly role: 'user' | 'assistant' | 'tool';
  readonly content?: SafeHtml;
  readonly text: string;
  readonly mode?: string;
  readonly chartData?: OrchestratorChartOutput;
  readonly chartStatus?: 'loading' | 'error';
  readonly chartToolCallId?: string;
  readonly chartToolName?: string;
  readonly toolContent?: ToolContent;
  readonly toolContentStatus?: 'loading' | 'error';
  readonly toolContentToolCallId?: string;
  readonly toolContentToolName?: string;
}

interface DataSourceLike {
  readonly projectId: string;
  readonly collectionId: string;
}

interface AskRequestParams {
  readonly input: string;
  readonly mode: 'ask' | 'edit';
  readonly dataSource: DataSourceLike | null;
  readonly messagesSignal: WritableSignal<AssistantMessageLike[]>;
  readonly spotlightAgentChatService: SpotlightAgentChatService;
  readonly beginRequest: () => number;
  readonly isRequestCurrent: (requestId: number) => boolean;
  readonly setActiveAbortController: (controller: AbortController | null) => void;
  readonly setWaitingForResponse: (isWaiting: boolean) => void;
  readonly setShowDataSourceSelector: (show: boolean) => void;
  readonly setCurrentSessionId: (sessionId: string) => void;
  readonly updateChatSessions: (
    updater: (sessions: ChatSessionWithName[]) => ChatSessionWithName[]
  ) => void;
  readonly addMessage: (message: {
    role: 'assistant' | 'user' | 'tool';
    text: string;
    mode: 'ask' | 'edit';
  }) => Promise<void>;
  readonly selectDataSourceText: string;
  readonly genericErrorText: string;
  readonly delegateCallbacks: AskDelegateCallbacks;
}

interface AskDelegateCallbacks {
  readonly emptyHtml: SafeHtml;
  readonly updateStreamingMessage: (index: number, text: string) => void;
  readonly insertToolPlaceholderAfter: (
    streamingMessageIndex: number | null,
    toolCallId: string | undefined,
    toolName: string
  ) => number;
  readonly resolveToolPlaceholder: (
    toolCallId: string | undefined,
    toolName: string,
    output: unknown
  ) => boolean;
  readonly addToolContentMessage: (toolName: string, output: unknown) => void;
  readonly normalizeToolOutput: (toolName: string, output: unknown) => unknown;
  readonly stripMarkdownImages: (text: string) => string;
  readonly setWaitingForResponse: (value: boolean) => void;
  readonly setTyping: (value: boolean) => void;
  readonly handleStreamingFallback: (
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    dataSource: unknown
  ) => void;
  readonly fetchToolOutputs: (sessionId: string) => void;
  readonly hasLoadingToolPlaceholders: () => boolean;
  readonly syncHistory: () => void;
  readonly addSessionExpiredMessage: () => void;
  readonly addErrorMessage: (errorText: string) => void;
}

interface CreateAskDelegateCallbacksParams {
  readonly askModeHelper: SpotlightAssistantAskModeHelper;
  readonly messagesSignal: WritableSignal<AssistantMessageLike[]>;
  readonly sanitizer: DomSanitizer;
  readonly emptyHtml: SafeHtml;
  readonly processMessage: (message: AssistantMessageLike) => Promise<AssistantMessageLike>;
  readonly addMessage: (message: AssistantMessageLike) => Promise<void>;
  readonly syncHistory: () => void;
  readonly spotlightAgentChatService: SpotlightAgentChatService;
  readonly setWaitingForResponse: (value: boolean) => void;
  readonly setTyping: (value: boolean) => void;
  readonly errorText: string;
  readonly sessionExpiredText: string;
}

/**
 * Helper for ask-mode request orchestration.
 */
export class SpotlightAssistantRequestHelper {
  constructor(private readonly logger: LoggerService) {}

  createAskDelegateCallbacks(params: CreateAskDelegateCallbacksParams): AskDelegateCallbacks {
    return {
      emptyHtml: params.emptyHtml,
      updateStreamingMessage: (index: number, text: string) =>
        params.askModeHelper.updateStreamingMessage(
          params.messagesSignal,
          params.sanitizer,
          index,
          text
        ),
      insertToolPlaceholderAfter: (
        streamingMessageIndex: number | null,
        toolCallId: string | undefined,
        toolName: string
      ) =>
        params.askModeHelper.insertToolPlaceholderAfter({
          messages: params.messagesSignal,
          streamingMessageIndex,
          toolCallId,
          toolName,
          emptyHtml: params.emptyHtml,
          syncHistory: () => params.syncHistory(),
        }),
      resolveToolPlaceholder: (toolCallId: string | undefined, toolName: string, output: unknown) =>
        params.askModeHelper.resolveToolPlaceholder({
          messages: params.messagesSignal,
          toolCallId,
          toolName,
          output,
          syncHistory: () => params.syncHistory(),
        }),
      addToolContentMessage: (toolName: string, output: unknown) => {
        params.askModeHelper.addToolContentMessage(
          toolName,
          output,
          params.processMessage,
          params.messagesSignal
        );
      },
      normalizeToolOutput: (toolName: string, output: unknown) =>
        params.askModeHelper.normalizeToolOutput(toolName, output),
      stripMarkdownImages: (text: string) => params.askModeHelper.stripMarkdownImages(text),
      setWaitingForResponse: (value: boolean) => params.setWaitingForResponse(value),
      setTyping: (value: boolean) => params.setTyping(value),
      handleStreamingFallback: (
        sessionId: string,
        messages: Array<{ role: 'user' | 'assistant'; content: string }>,
        dataSource: unknown
      ) => {
        params.askModeHelper.handleStreamingFallback({
          sessionId,
          messages: messages as ChatMessage[],
          dataSource: dataSource as { projectId: string; collectionId: string },
          spotlightAgentChatService: params.spotlightAgentChatService,
          addMessage: (message) => params.addMessage(message as AssistantMessageLike),
          addToolContentMessage: async (toolName: string, output: unknown) => {
            await params.askModeHelper.addToolContentMessage(
              toolName,
              output,
              params.processMessage,
              params.messagesSignal
            );
          },
          addChartMessage: async (chartData) => {
            await params.askModeHelper.addChartMessage(
              chartData,
              params.processMessage,
              params.messagesSignal
            );
          },
          normalizeToolOutput: (toolName: string, output: unknown) =>
            params.askModeHelper.normalizeToolOutput(toolName, output),
          normalizeChartOutput: (output: unknown) =>
            params.askModeHelper.normalizeChartOutput(output),
          errorText: params.errorText,
        });
      },
      fetchToolOutputs: (sessionId: string) => {
        params.askModeHelper.fetchToolOutputs({
          messages: params.messagesSignal,
          sessionId,
          spotlightAgentChatService: params.spotlightAgentChatService,
          normalizeToolOutput: (toolName: string, output: unknown) =>
            params.askModeHelper.normalizeToolOutput(toolName, output),
          resolveToolPlaceholder: (
            toolCallId: string | undefined,
            toolName: string,
            output: unknown
          ) =>
            params.askModeHelper.resolveToolPlaceholder({
              messages: params.messagesSignal,
              toolCallId,
              toolName,
              output,
              syncHistory: () => params.syncHistory(),
            }),
          addToolContentMessage: async (toolName: string, output: unknown) => {
            await params.askModeHelper.addToolContentMessage(
              toolName,
              output,
              params.processMessage,
              params.messagesSignal
            );
          },
          markUnresolvedChartPlaceholdersAsError: () =>
            params.askModeHelper.markUnresolvedChartPlaceholdersAsError({
              messages: params.messagesSignal,
              syncHistory: () => params.syncHistory(),
            }),
          markUnresolvedToolPlaceholdersAsError: () =>
            params.askModeHelper.markUnresolvedToolPlaceholdersAsError({
              messages: params.messagesSignal,
              syncHistory: () => params.syncHistory(),
            }),
        });
      },
      hasLoadingToolPlaceholders: () =>
        params
          .messagesSignal()
          .some(
            (message) =>
              message.chartStatus === 'loading' || message.toolContentStatus === 'loading'
          ),
      syncHistory: () => params.syncHistory(),
      addSessionExpiredMessage: () => {
        params.addMessage({
          role: 'assistant',
          text: params.sessionExpiredText,
          mode: 'ask',
        });
      },
      addErrorMessage: (errorText: string) => {
        params.addMessage({
          role: 'assistant',
          text: errorText || params.errorText,
          mode: 'ask',
        });
      },
    };
  }

  async sendAskRequest(params: AskRequestParams): Promise<void> {
    const dataSource = params.dataSource;
    if (!dataSource) {
      await params.addMessage({
        role: 'assistant',
        text: params.selectDataSourceText,
        mode: params.mode,
      });
      params.setShowDataSourceSelector(true);
      return;
    }
    const requestId = params.beginRequest();
    const nextAbortController = new AbortController();
    params.setActiveAbortController(nextAbortController);
    try {
      const chatSession = await params.spotlightAgentChatService.getOrCreateSession(
        dataSource.projectId,
        dataSource.collectionId
      );
      if (!params.isRequestCurrent(requestId) || nextAbortController.signal.aborted) {
        return;
      }
      params.spotlightAgentChatService.setCurrentSession(chatSession);
      params.setCurrentSessionId(chatSession.id);
      params.updateChatSessions((sessions) => {
        const exists = sessions.some((session) => session.id === chatSession.id);
        return exists ? sessions : [chatSession as ChatSessionWithName, ...sessions];
      });
      const chatMessages: Array<{ role: 'user' | 'assistant'; content: string }> = params
        .messagesSignal()
        .filter(
          (message) =>
            message.mode === 'ask' && message.role !== 'tool' && message.text.trim().length > 0
        )
        .map((message) => ({
          role: message.role as 'user' | 'assistant',
          content: message.text,
        }));
      params.setWaitingForResponse(true);
      const orchestrator = new StreamingChatOrchestrator(
        params.messagesSignal,
        this.buildStreamingDelegate(params.delegateCallbacks),
        chatSession.id,
        chatMessages,
        dataSource,
        this.logger
      );
      await params.spotlightAgentChatService.sendStreamingMessage(
        chatSession.id,
        chatMessages,
        orchestrator.createCallbacks(),
        nextAbortController.signal
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      this.logger.error('Ask mode error:', { error });
      params.setWaitingForResponse(false);
      const message = error instanceof Error ? error.message : params.genericErrorText;
      await params.addMessage({
        role: 'assistant',
        text: message || params.genericErrorText,
        mode: 'ask',
      });
    } finally {
      if (params.isRequestCurrent(requestId)) {
        params.setActiveAbortController(null);
      }
    }
  }

  private buildStreamingDelegate(callbacks: AskDelegateCallbacks): StreamingChatDelegate {
    return {
      createEmptyAssistantMessage: () => ({
        role: 'assistant' as const,
        text: '',
        content: callbacks.emptyHtml,
        mode: 'ask',
      }),
      updateStreamingMessage: callbacks.updateStreamingMessage,
      insertToolPlaceholderAfter: callbacks.insertToolPlaceholderAfter,
      resolveToolPlaceholder: callbacks.resolveToolPlaceholder,
      addToolContentMessage: callbacks.addToolContentMessage,
      normalizeToolOutput: callbacks.normalizeToolOutput,
      stripMarkdownImages: callbacks.stripMarkdownImages,
      setWaitingForResponse: callbacks.setWaitingForResponse,
      setTyping: callbacks.setTyping,
      handleStreamingFallback: callbacks.handleStreamingFallback,
      fetchToolOutputs: callbacks.fetchToolOutputs,
      hasLoadingToolPlaceholders: callbacks.hasLoadingToolPlaceholders,
      syncHistory: callbacks.syncHistory,
      addSessionExpiredMessage: callbacks.addSessionExpiredMessage,
      addErrorMessage: callbacks.addErrorMessage,
    };
  }
}
